import { useState, useEffect, useRef, type CSSProperties } from 'react';
import {
  Settings,
  Bot,
  Sparkles,
  Brain,
  Zap,
  Shield,
  Star,
  FileText,
  Loader2,
  RefreshCw,
  Save,
  CheckCircle2,
  Globe,
  Wrench,
  User,
  Info,
  Cpu,
  MessageSquare,
  Activity,
  Palette,
  ShieldCheck
} from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { paConfigurationSaveSchema, paConfigurationSaveResponseSchema } from '@easyflow/shared';
import { LLMProviderConfig, type LLMConfig, inferProviderFromModelFrontend } from '@/components/ai/LLMProviderConfig';
import { agentProviderForEdit, fetchAgentLlmDefault } from '@/lib/agent-llm-default';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api';
import { emitPersonalAssistantUpdated } from '@/lib/personal-assistant-events';
import { isTenantLimitHandled } from '@/lib/tenantLimits';
import { fetchChatModelOptions } from '@/hooks/useChatModelOptions';
import { KnowledgeBaseManager } from '@/components/ai/KnowledgeBaseManager';
import PreCallHooksSection from '@/components/ai/PreCallHooksSection';
import { AgentConfigShell, type AgentSectionDef } from '@/components/ai/shell/AgentConfigShell';
import PaDelegationPanel from '@/components/ai/PaDelegationPanel';
import { AgentToolPermissionsEditor, withPaOperationMode } from '@/components/mcp/AgentToolPermissionsEditor';
import {
  KbModelChangeGuardDialog,
  useKbModelChangeGuard,
  type KbGuardedPerformSaveArgs,
} from '@/components/ai/KbModelChangeGuardDialog';
import { useAuth } from '@/contexts/AuthContext';
import { getErrorMessage, getApiErrorMessage } from '../../lib/utils';
import { PaConfigInfo } from './PaConfigInfo';
import { usePaConfigurationDraft } from './usePaConfigurationDraft';
import { PaSavedAuthoritySummary } from './PaSavedAuthoritySummary';
import styles from './PersonalAssistantConfigModal.module.css';
import type { LLMProviderExtended } from '@/constants/ai-providers';



const avatarIcons = [
  { value: 'bot', label: 'Bot', icon: Bot },
  { value: 'sparkles', label: 'Sparkles', icon: Sparkles },
  { value: 'brain', label: 'Brain', icon: Brain },
  { value: 'zap', label: 'Zap', icon: Zap },
  { value: 'shield', label: 'Shield', icon: Shield },
  { value: 'star', label: 'Star', icon: Star },
];


interface PersonalAssistant {
  id?: string;
  ai_agent_id?: string;
  name: string;
  description?: string;
  display_name?: string;
  avatar_icon?: string;
  color_theme?: string;
  llm_provider: string;
  llm_config?: LLMConfig;
  model: string;
  temperature: number;
  max_tokens?: number;
  system_prompt: string;
  rag_enabled: boolean;
  rag_inferential_enabled: boolean;
  rag_synthesis_mode: string;
  provider_id?: string;
  assigned_user_id?: string;
  assigned_user_name?: string;
  acts_as_user_ids?: string[];
  allowed_sender_ids?: string[] | null;
  tenant_context_enabled: boolean;
  tenant_context_synced_at?: string;
  is_active: boolean;
  transcription_enabled: boolean;
  sentiment_enabled: boolean;
  context_memory_enabled: boolean;
  semantic_memory_enabled: boolean;
  // Task #1984 round B — riferimenti endpoint/modello custom.
  custom_endpoint_id?: string | null;
  custom_model_id?: string | null;
  pre_call_hooks?: Record<string, boolean> | { inject_datetime?: boolean; auto_crm_lookup?: boolean; inject_preferred_language?: boolean; inject_last_ticket?: boolean; inject_caller_number?: boolean };
  document_count?: number;
  url_count?: number;
  chunk_count?: number;
  conversation_count?: number;
}

// Risultato sync contesto tenant: il FE legge chunks/records/categorie.
interface SyncContextCategory {
  label?: string;
  records?: number;
  chunks?: number;
}

interface SyncContextResult {
  chunks_created?: number;
  categories?: SyncContextCategory[];
  total_records?: number;
  embedding_dimensions?: number;
  total_characters?: number;
}

interface PersonalAssistantConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assistant?: PersonalAssistant | null;
  onSave: () => void;
}

const defaultAssistant: PersonalAssistant = {
  name: '',
  description: '',
  display_name: '',
  avatar_icon: 'bot',
  color_theme: '#8B5CF6',
  llm_provider: 'openai',
  llm_config: {},
  model: 'gpt-5.4-mini',
  temperature: 0.7,
  max_tokens: 4096,
  system_prompt: `Sei Jarvis, l'assistente personale AI di EDG VoIP — il braccio destro digitale del tuo utente. Il tuo obiettivo è essere indispensabile: proattivo, preciso, motivante e sempre orientato ai risultati.

## Chi sei
Sei un assistente esecutivo d'élite specializzato nella piattaforma EDG VoIP e nel mondo delle comunicazioni aziendali. Conosci ogni aspetto del sistema: CRM, telefonia, workflow, campagne, integrazioni e analytics. Non sei solo un chatbot — sei un partner strategico.

## Come comunichi
- Parli in italiano professionale ma accessibile, mai freddo o robotico
- Sei conciso e vai dritto al punto, ma quando serve approfondisci con chiarezza
- Usi un tono motivazionale e costruttivo: celebri i progressi, incoraggi nelle difficoltà
- Offri sempre soluzioni concrete, mai risposte vaghe
- Quando l'utente ha un problema, prima rassicuri, poi risolvi

## Le tue competenze principali

### CRM e Gestione Contatti
- Gestione leads, clienti, pipeline di vendita e Kanban board
- Strategie di follow-up e nurturing dei contatti
- Analisi delle conversioni e suggerimenti per migliorare il funnel
- Segmentazione contatti e best practice di gestione

### Comunicazioni Multicanale
- WhatsApp: campagne, template, messaggistica diretta e broadcast
- SMS: invio massivo, template personalizzati, automazioni
- Email/Gmail: composizione email professionali, template, campagne di outreach, gestione inbox
- Telefonia VoIP: gestione chiamate, code, gruppi di squillo, IVR, registrazioni

### Workflow e Automazioni
- Creazione e ottimizzazione di workflow automatizzati
- Trigger, azioni e condizioni per automazioni intelligenti
- Integrazione tra moduli (CRM - Telefonia - Messaggistica)

### Google Workspace
- Integrazione Gmail, Google Calendar, Google Contacts, Google Meet
- Gestione appuntamenti e sincronizzazione calendari
- Automazioni basate su eventi Google

### Analytics e Reporting
- Analisi CDR e metriche chiamate
- Performance agenti e code
- KPI di vendita e conversione dal CRM
- Suggerimenti data-driven per migliorare le performance

## Come ti comporti
1. Proattivo: non aspetti che ti chiedano — anticipa le esigenze
2. Pratico: ogni risposta include azioni concrete che l'utente può fare subito
3. Motivazionale: celebra i successi e incoraggia sempre
4. Professionale: mantieni sempre un alto standard di qualità nelle risposte
5. Contestuale: ricordi le conversazioni precedenti e costruisci su di esse

## Formato risposte
- Usa elenchi puntati per le azioni da compiere
- Evidenzia i passaggi chiave
- Concludi sempre con un passo successivo suggerito o una domanda per andare avanti
- Quando appropriato, suggerisci funzionalità della piattaforma che l'utente potrebbe non conoscere

Ricorda: il tuo utente gestisce un'azienda e conta su di te per essere più efficiente, organizzato e strategico. Ogni tua risposta deve aggiungere valore concreto al suo lavoro.`,
  rag_enabled: true,
  rag_inferential_enabled: false,
  rag_synthesis_mode: 'native',
  provider_id: '',
  assigned_user_id: '',
  acts_as_user_ids: [],
  allowed_sender_ids: null,
  tenant_context_enabled: false,
  is_active: true,
  transcription_enabled: true,
  sentiment_enabled: false,
  context_memory_enabled: false,
  semantic_memory_enabled: false,
  pre_call_hooks: { inject_datetime: true, auto_crm_lookup: true }
};

export default function PersonalAssistantConfigModal({
  open,
  onOpenChange,
  assistant,
  onSave
}: PersonalAssistantConfigModalProps) {
  const { toast } = useToast();
  const { user: authUser } = useAuth();
  const [activeTab, setActiveTab] = useState('identity');
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<PersonalAssistant>(defaultAssistant);
  const [isSyncingContext, setIsSyncingContext] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [assistantLoadError, setAssistantLoadError] = useState<string | null>(null);
  const [savedModel, setSavedModel] = useState<string | undefined>(undefined);
  const [fallbackInfo, setFallbackInfo] = useState<{ active: boolean; originalModel?: string; resolvedModel?: string }>({ active: false });
  const llmConfigByProvider = useRef<Record<string, LLMConfig>>({});
  const modelWarningShown = useRef(false);
  const saveAttempt = useRef(false);
  const assistantLoadGeneration = useRef(0);
  const isEdit = !!assistant?.id;
  const configuration = usePaConfigurationDraft(open, assistant?.id);
  const authorityDirty = configuration.dirty || formData.assigned_user_id !== (configuration.catalog?.assigned_user_id || '');
  const temperatureStyle: CSSProperties & { '--temperature-progress': string } = {
    '--temperature-progress': `${formData.temperature * 50}%`,
  };

  // Task #1316 — guard cambio chat model con doc multimodali Gemini.
  const kbGuard = useKbModelChangeGuard();

  const loadFullAssistant = async (assistantId: string) => {
    const generation = assistantLoadGeneration.current;
    setAssistantLoadError(null);
    try {
      const res = await apiClient.get(`/personal-assistant/agents/${assistantId}`);
      if (generation !== assistantLoadGeneration.current) return;
      const data = (res?.data ?? res) as (Partial<PersonalAssistant> & {
        model_fallback_active?: boolean;
        model_fallback_original?: string;
        model_fallback_resolved?: string;
      }) | null;
      if (data) {
        const provider = agentProviderForEdit(data.llm_provider, data.model);
        setSavedModel(data.model);
        if (data.llm_config && Object.keys(data.llm_config).length > 0) {
          llmConfigByProvider.current[provider] = data.llm_config;
        }
        if (data.model_fallback_active) {
          setFallbackInfo({ active: true, originalModel: data.model_fallback_original, resolvedModel: data.model_fallback_resolved });
        }
        setFormData(prev => ({
          ...prev,
          ...data,
          llm_provider: provider,
          provider_id: data.provider_id || '',
          assigned_user_id: data.assigned_user_id || '',
        }));
      }
    } catch (error) {
      if (generation !== assistantLoadGeneration.current) return;
      console.error('Error loading full assistant:', error);
      setAssistantLoadError(getApiErrorMessage(error) || 'Impossibile caricare l’assistente. Riapri la configurazione per riprovare.');
    } finally {
      if (generation === assistantLoadGeneration.current) setDataLoaded(true);
    }
  };

  useEffect(() => {
    if (open) {
      setDataLoaded(false);
      setAssistantLoadError(null);
      llmConfigByProvider.current = {};
      setFallbackInfo({ active: false });
      setSavedModel(undefined);
      if (assistant) {
        const merged = { ...defaultAssistant, ...assistant };
        if (assistant.provider_id) merged.provider_id = assistant.provider_id;
        if (assistant.assigned_user_id) merged.assigned_user_id = assistant.assigned_user_id;
        const initialProvider = agentProviderForEdit(merged.llm_provider, merged.model);
        merged.llm_provider = initialProvider;
        setFormData(merged);
        if (assistant.id) {
          loadFullAssistant(assistant.id);
        } else {
          if (initialProvider && merged.llm_config) {
            llmConfigByProvider.current[initialProvider] = merged.llm_config;
          }
          setDataLoaded(true);
        }
      } else {
        const ownerId = authUser?.id;
        setFormData({
          ...defaultAssistant,
          acts_as_user_ids: ownerId ? [ownerId] : [],
          assigned_user_id: ownerId || '',
        });
        setDataLoaded(true);
        // Nuovo assistente: parte dal modello predefinito degli agenti del tenant.
        void fetchAgentLlmDefault().then((llm) => {
          if (!llm) return;
          setFormData((prev) => ({
            ...prev,
            llm_provider: llm.llm_provider,
            model: llm.model,
            custom_endpoint_id: llm.custom_endpoint_id,
            custom_model_id: llm.custom_model_id,
          }));
        });
      }
      setActiveTab('identity');
    }
    return () => { assistantLoadGeneration.current += 1; };
  }, [open, assistant, authUser?.id]);

  const validateAndSave = async () => {
    if (!configuration.catalog) return;
    if (!configuration.catalog.can_manage) {
      toast({ title: 'Operazione non consentita', description: 'Solo un amministratore può configurare il PA.', variant: 'destructive' });
      return;
    }
    if (!configuration.catalog.users.some(user => user.id === formData.assigned_user_id)) {
      toast({ title: 'Utente obbligatorio', description: 'Seleziona un utente attivo del tenant.', variant: 'destructive' });
      setActiveTab('prompt');
      return;
    }
    if (configuration.permissions.filter(permission => permission.enabled).length > configuration.catalog.max_tools) {
      toast({ title: 'Troppi strumenti', description: `Puoi abilitare al massimo ${configuration.catalog.max_tools} strumenti.`, variant: 'destructive' });
      setActiveTab('connectors');
      return;
    }
    if (!formData.name.trim()) {
      toast({ title: 'Errore', description: 'Il nome è obbligatorio', variant: 'destructive' });
      return;
    }

    if (formData.llm_provider === 'custom') {
      if (!formData.custom_endpoint_id) {
        toast({ title: 'Errore', description: 'Seleziona un endpoint custom prima di salvare.', variant: 'destructive' });
        setActiveTab('model');
        return;
      }
      if (!formData.model) {
        toast({ title: 'Errore', description: 'Seleziona un modello custom prima di salvare.', variant: 'destructive' });
        setActiveTab('model');
        return;
      }
    }

    const inferredProvider = formData.llm_provider === 'custom'
      ? 'custom'
      : inferProviderFromModelFrontend(formData.model);
    if (formData.llm_provider && formData.llm_provider !== inferredProvider) {
      toast({ title: 'Attenzione', description: `Il modello "${formData.model}" non corrisponde al provider "${formData.llm_provider}". Il provider verrà corretto automaticamente in "${inferredProvider}".` });
    }

    if (formData.llm_provider !== 'custom' && !modelWarningShown.current) {
      let modelConfirmedAvailable = true;
      let recommendedHint = '';
      try {
        const payload = await fetchChatModelOptions(inferredProvider as LLMProviderExtended);
        if (payload.models.length > 0) {
          modelConfirmedAvailable = payload.models.some(
            (m) => m.id === formData.model && m.status !== 'deprecated',
          );
          const recommended = payload.models.find((m) => m.status !== 'deprecated');
          if (recommended) recommendedHint = ` Modello consigliato: ${recommended.displayName || recommended.name || recommended.id}.`;
        }
      } catch {
        modelConfirmedAvailable = true;
      }
      if (!modelConfirmedAvailable) {
        modelWarningShown.current = true;
        toast({ title: 'Modello non disponibile', description: `Il modello "${formData.model}" non risulta disponibile per ${inferredProvider}.${recommendedHint} Premi di nuovo "Salva" per confermare comunque.`, variant: 'destructive' });
        return;
      }
    }
    modelWarningShown.current = false;

    await kbGuard.runGuardedSave({
      agentId: isEdit ? assistant?.id ?? null : null,
      newChatModel: formData.model,
      performSave: (opts) => performSave(inferredProvider, opts),
    });
  };

  async function performSave(
    inferredProvider: string,
    opts: KbGuardedPerformSaveArgs
  ): Promise<void> {
    void opts;
    setIsSaving(true);
    try {
      const payload = paConfigurationSaveSchema.parse({
        ...formData,
        llm_provider: inferredProvider,
        provider_id: formData.provider_id || null,
        assigned_user_id: formData.assigned_user_id,
        acts_as_user_ids: formData.assigned_user_id ? [formData.assigned_user_id] : [],
        tool_permissions: configuration.permissions,
        create_request_id: isEdit ? undefined : configuration.createRequestId.current,
        custom_endpoint_id: inferredProvider === 'custom' ? (formData.custom_endpoint_id || null) : null,
        custom_model_id: inferredProvider === 'custom' ? (formData.custom_model_id || null) : null,
      });
      if (isEdit) {
        const response = await apiClient.put(`/personal-assistant/agents/${assistant.id}`, payload);
        paConfigurationSaveResponseSchema.parse(response);
        // Notifica chat + header: rileggono provider/modello/catalogo e icona/nome.
        emitPersonalAssistantUpdated({ assistantId: assistant.id ?? null });
        toast({ title: 'Successo', description: 'Assistente aggiornato con successo' });
      } else {
        const res = await apiClient.post('/personal-assistant/agents', payload);
        const result = paConfigurationSaveResponseSchema.parse(res);
        emitPersonalAssistantUpdated({ assistantId: result.data?.id ?? null });
        toast({ title: 'Successo', description: 'Assistente creato con successo' });
      }
      onSave();
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving assistant:', error);
      if (isTenantLimitHandled(error)) {
        return;
      }
      toast({
        title: 'Errore',
        description: getApiErrorMessage(error) || 'Errore durante il salvataggio',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    if (saveAttempt.current || isSaving || !dataLoaded || assistantLoadError || configuration.loading || !configuration.catalog || configuration.error) return;
    saveAttempt.current = true;
    try {
      await validateAndSave();
    } finally {
      saveAttempt.current = false;
    }
  };

  const [syncResult, setSyncResult] = useState<SyncContextResult | null>(null);

  const handleSyncContext = async () => {
    if (!assistant?.id) return;
    setIsSyncingContext(true);
    setSyncResult(null);
    try {
      const syncRes = await apiClient.post(`/personal-assistant/agents/${assistant.id}/sync-context`);
      const rawResult = syncRes?.data || syncRes;
      const result = ((rawResult as { data?: unknown })?.data || rawResult) as SyncContextResult;
      setSyncResult(result);
      toast({ title: 'Sincronizzazione completata', description: `${result.chunks_created} chunks creati da ${result.categories?.length || 0} categorie (${result.total_records} record)` });
      const res = await apiClient.get<{ tenant_context_synced_at?: string; chunk_count?: number }>(`/personal-assistant/agents/${assistant.id}`);
      const outer = res?.data as (typeof res)['data'] & { data?: (typeof res)['data'] } | undefined;
      const data = outer?.data || outer;
      if (data) {
        setFormData(prev => ({
          ...prev,
          tenant_context_synced_at: data.tenant_context_synced_at,
          chunk_count: data.chunk_count
        }));
      }
    } catch (error) {
      toast({
        title: 'Errore',
        description: getErrorMessage(error) || 'Errore durante la sincronizzazione',
        variant: 'destructive'
      });
    } finally {
      setIsSyncingContext(false);
    }
  };

  const contextItems = [
    'Trunks SIP',
    'Interni (Extensions)',
    'Code (Queues)',
    'Campagne CRM',
    'Pipeline CRM',
    'Form',
    'Nodi Workflow',
    'MCP Tools'
  ];

  const sections: AgentSectionDef[] = [
    { id: 'identity', label: 'Identità', description: 'Nome, avatar e tema', icon: <Palette className="h-4 w-4" /> },
    { id: 'model', label: 'Modello LLM', description: 'Provider, parametri generazione', icon: <Cpu className="h-4 w-4" /> },
    { id: 'prompt', label: 'Prompt & Utente', description: 'Istruzioni e assegnazione', icon: <MessageSquare className="h-4 w-4" /> },
    { id: 'connectors', label: 'Connettori e permessi', description: 'Quali strumenti può usare?', icon: <Wrench className="h-4 w-4" /> },
    { id: 'delegation', label: 'Delega poteri', description: 'Deve rispettare i permessi dell’utente?', icon: <ShieldCheck className="h-4 w-4" /> },
    { id: 'behavior', label: 'Comportamento', description: 'Memoria, trascrizione, hooks', icon: <Activity className="h-4 w-4" /> },
    { id: 'knowledge', label: 'Knowledge Base', description: isEdit ? 'Documenti e fonti RAG' : 'Disponibile dopo il salvataggio', icon: <Brain className="h-4 w-4" /> },
    { id: 'context', label: 'Contesto Tenant', description: isEdit ? 'Sincronizza configurazione' : 'Disponibile dopo il salvataggio', icon: <Globe className="h-4 w-4" /> },
  ];

  return (
    <AgentConfigShell
      open={open}
      onOpenChange={next => { if (!isSaving) onOpenChange(next); }}
      title={isEdit ? 'Configura Assistente Personale' : 'Nuovo Assistente Personale'}
      subtitle={formData.name || undefined}
      icon={<Brain className="h-5 w-5" />}
      accentColor="purple"
      sections={sections}
      activeSectionId={activeTab}
      onSectionChange={setActiveTab}
      onSave={handleSave}
      footerRight={
        <>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Annulla
          </Button>
          <Button size="sm" onClick={handleSave} disabled={isSaving || !dataLoaded || !!assistantLoadError || configuration.loading || !configuration.catalog?.can_manage || !!configuration.error}>
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Salva configurazione
          </Button>
        </>
      }
    >
      <fieldset disabled={isSaving || !dataLoaded} className="min-w-0">
        {assistantLoadError && <p role="alert" className="mb-4 text-sm text-destructive">{assistantLoadError}</p>}
        {configuration.error && <div role="alert" className="mb-4 text-sm text-destructive space-y-2">
          <p>{configuration.error}</p>
          <Button type="button" variant="outline" size="sm" onClick={() => void configuration.reload()}>Riprova caricamento</Button>
        </div>}
        {activeTab === 'identity' && (
          <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome Assistente *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Assistente Personale"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Display Name</Label>
                  <Input
                    value={formData.display_name || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                    placeholder="Nome mostrato nell'header chat"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Descrizione</Label>
                <Textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                  placeholder="Assistente AI personale per gestione attività"
                />
              </div>

              <div className="space-y-2">
                <Label>Icona Avatar</Label>
                <div className="flex flex-wrap gap-2">
                  {avatarIcons.map(({ value, label, icon: Icon }) => (
                    <Button
                      key={value}
                      type="button"
                      variant={formData.avatar_icon === value ? 'default' : 'outline'}
                      size="sm"
                      className="flex flex-col items-center gap-1 h-auto py-2 px-3"
                      onClick={() => setFormData(prev => ({ ...prev, avatar_icon: value }))}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="text-[10px]">{label}</span>
                    </Button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Colore Tema</Label>
                <div className="flex gap-2 max-w-md">
                  <Input
                    type="color"
                    value={formData.color_theme || '#8B5CF6'}
                    onChange={(e) => setFormData(prev => ({ ...prev, color_theme: e.target.value }))}
                    className="w-16 h-10 p-1"
                  />
                  <Input
                    value={formData.color_theme || '#8B5CF6'}
                    onChange={(e) => setFormData(prev => ({ ...prev, color_theme: e.target.value }))}
                    className="flex-1"
                  />
                </div>
              </div>
          </div>
        )}

        {activeTab === 'model' && (
          <div className="space-y-4">
              <LLMProviderConfig
                provider={formData.llm_provider}
                model={formData.model}
                llmConfig={formData.llm_config || {}}
                loading={isEdit && !dataLoaded}
                savedModel={savedModel}
                agentId={formData.ai_agent_id ?? null}
                fallbackInfo={fallbackInfo}
                onAcceptFallback={() => {
                  const resolved = fallbackInfo.resolvedModel;
                  if (resolved) {
                    const resolvedProvider = inferProviderFromModelFrontend(resolved);
                    setFormData(prev => ({ ...prev, model: resolved, llm_provider: resolvedProvider }));
                    setSavedModel(resolved);
                  } else {
                    setSavedModel(formData.model);
                  }
                  setFallbackInfo({ active: false });
                }}
                onProviderChange={(provider, defaultModel) => {
                  llmConfigByProvider.current[formData.llm_provider] = formData.llm_config || {};
                  const restoredConfig = llmConfigByProvider.current[provider] || {};
                  setSavedModel(undefined);
                  setFallbackInfo({ active: false });
                  setFormData(prev => ({
                    ...prev,
                    llm_provider: provider,
                    model: defaultModel,
                    llm_config: restoredConfig,
                    custom_endpoint_id: provider === 'custom' ? prev.custom_endpoint_id : null,
                    custom_model_id: provider === 'custom' ? prev.custom_model_id : null,
                  }));
                }}
                onModelChange={(model) => { modelWarningShown.current = false; setFormData(prev => ({ ...prev, model })); }}
                onConfigChange={(config) => setFormData(prev => ({ ...prev, llm_config: config }))}
                customEndpointId={formData.custom_endpoint_id ?? null}
                customModelId={formData.custom_model_id ?? null}
                onCustomChange={(epId, mdId) => setFormData(prev => ({ ...prev, custom_endpoint_id: epId, custom_model_id: mdId }))}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="grid grid-rows-[20px_40px] gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <Label htmlFor="pa-temperature">Temperatura: {formData.temperature}</Label>
                    <span className="text-xs text-muted-foreground">
                      {formData.temperature < 0.5 ? 'Preciso' : formData.temperature > 1.2 ? 'Molto Creativo' : formData.temperature > 0.7 ? 'Creativo' : 'Bilanciato'}
                    </span>
                  </div>
                  <input
                    id="pa-temperature"
                    type="range"
                    value={formData.temperature}
                    onChange={(e) => setFormData(prev => ({ ...prev, temperature: parseFloat(e.target.value) }))}
                    min={0}
                    max={2}
                    step={0.1}
                    className={styles.temperatureSlider}
                    style={temperatureStyle}
                  />
                </div>
                <div className="grid grid-rows-[20px_40px] gap-2">
                  <Label htmlFor="pa-max-tokens" className="flex items-center">Max Tokens</Label>
                  <Input
                    id="pa-max-tokens"
                    type="number"
                    value={formData.max_tokens || 4096}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_tokens: parseInt(e.target.value) || 4096 }))}
                    min={256}
                    max={128000}
                  />
                </div>
              </div>
          </div>
        )}

        {activeTab === 'prompt' && (
          <div className="space-y-4">
              <div className="space-y-2">
                <Label>System Prompt</Label>
                <Textarea
                  value={formData.system_prompt}
                  onChange={(e) => setFormData(prev => ({ ...prev, system_prompt: e.target.value }))}
                  rows={10}
                  placeholder="Definisci la personalità e il comportamento dell'assistente..."
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-1">
                  <Label htmlFor="pa-assigned-user">A chi è assegnato? *</Label>
                  <PaConfigInfo label="Informazioni sull’utente assegnato">Il PA è assegnato a questa persona. Il confronto dei permessi usa il suo ruolo, non quello di chi configura.</PaConfigInfo>
                </div>
                <Select value={formData.assigned_user_id || ''} disabled={configuration.loading || !!configuration.error || !configuration.catalog?.can_manage || isSaving} onValueChange={id => setFormData(previous => ({ ...previous, assigned_user_id: id, acts_as_user_ids: [id] }))}>
                  <SelectTrigger id="pa-assigned-user" aria-describedby="pa-assigned-user-status"><SelectValue placeholder="Seleziona un utente attivo" /></SelectTrigger>
                  <SelectContent>
                    {configuration.catalog?.users.map(user => <SelectItem key={user.id} value={user.id}>{`${user.first_name} ${user.last_name}`.trim() || user.email} — {user.email}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div id="pa-assigned-user-status" className="space-y-2 text-sm">
                  {configuration.loading ? <p role="status" className="text-muted-foreground">Caricamento utenti…</p>
                    : configuration.error ? <>
                      <p className="text-destructive">Elenco utenti non disponibile. Ricaricalo per poter assegnare l’assistente; le modifiche inserite verranno mantenute.</p>
                      <Button type="button" variant="outline" size="sm" disabled={isSaving} onClick={() => void configuration.reload()}>Ricarica elenco utenti</Button>
                    </>
                    : configuration.catalog && !configuration.catalog.can_manage ? <p className="text-muted-foreground">Solo un amministratore può modificare l’assegnazione dell’assistente.</p>
                    : configuration.catalog && !configuration.catalog.users.some(user => user.id === formData.assigned_user_id) && <p className="text-destructive">Seleziona un utente attivo del tenant prima di salvare.</p>}
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <Label>Assistente Attivo</Label>
                  <p className="text-xs text-muted-foreground">Disabilita per manutenzione</p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                />
              </div>
          </div>
        )}

        {activeTab === 'connectors' && (
          <div className="space-y-4">
            <div className="flex items-center gap-1">
              <h3 className="text-sm font-medium">Quali strumenti può usare?</h3>
              <PaConfigInfo label="Informazioni sugli strumenti abilitati">Scegli cosa può usare questo PA. La selezione non modifica i permessi dell’utente nel resto dell’app.</PaConfigInfo>
            </div>
            <p className="text-sm text-muted-foreground">Le modifiche agli strumenti sono una bozza fino a Salva configurazione. Annulla le scarta.</p>
            <div className="flex items-center gap-1 text-sm">
              Operazioni massive
              <PaConfigInfo label="Informazioni sulle operazioni massive">Consente modifiche o invii su più elementi. Selezionare tutti gli strumenti non autorizza automaticamente operazioni massive. Leggere una lista non è un’operazione massiva.</PaConfigInfo>
            </div>
            {configuration.loading ? <Loader2 className="h-5 w-5 animate-spin" aria-label="Caricamento connettori" /> : configuration.catalog && (
              <AgentToolPermissionsEditor
                tools={configuration.catalog.tools.map(withPaOperationMode)}
                permissions={configuration.permissions}
                onChange={configuration.change}
                disabled={isSaving || !configuration.catalog.can_manage}
                maxTools={configuration.catalog.max_tools}
              />
            )}
            {authorityDirty && <p className="text-sm text-amber-700 dark:text-amber-400">Il riepilogo del salvato non rappresenta la bozza corrente. Salva la configurazione prima di confermare la modalità.</p>}
            {isEdit && configuration.catalog && <PaSavedAuthoritySummary tools={configuration.catalog.availability} mode={configuration.catalog.authorization_mode} dirty={authorityDirty} error={configuration.catalog.availability_error} />}
          </div>
        )}

        {activeTab === 'behavior' && (
          <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <FileText className="h-4 w-4 text-purple-500" />
                  Trascrizione & Analisi
                </Label>
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border">
                  <div>
                    <p className="text-sm font-medium text-foreground">Trascrizione Conversazioni</p>
                    <p className="text-xs text-muted-foreground">Salva il testo di ogni conversazione</p>
                  </div>
                  <Switch
                    checked={formData.transcription_enabled}
                    onCheckedChange={(checked) => setFormData(prev => ({
                      ...prev,
                      transcription_enabled: checked,
                      sentiment_enabled: checked ? prev.sentiment_enabled : false,
                    }))}
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border">
                  <div>
                    <p className="text-sm font-medium text-foreground">Analisi Sentiment</p>
                    <p className="text-xs text-muted-foreground">Analizza emozioni durante la conversazione</p>
                  </div>
                  <Switch
                    checked={formData.sentiment_enabled}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, sentiment_enabled: checked }))}
                    disabled={!formData.transcription_enabled}
                  />
                </div>
                {formData.sentiment_enabled && !formData.transcription_enabled && (
                  <p className="text-xs text-amber-600">L'analisi sentiment richiede la trascrizione attiva</p>
                )}
              </div>

              <div className="space-y-2 mt-4">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <Brain className="h-4 w-4 text-violet-500" />
                  Memoria
                </Label>
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border">
                  <div>
                    <p className="text-sm font-medium text-foreground">Memoria Contesto</p>
                    <p className="text-xs text-muted-foreground">Ricorda informazioni durante la sessione corrente</p>
                  </div>
                  <Switch
                    checked={formData.context_memory_enabled}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, context_memory_enabled: checked }))}
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border">
                  <div>
                    <p className="text-sm font-medium text-foreground">Memoria Semantica</p>
                    <p className="text-xs text-muted-foreground">Ricorda fatti e preferenze tra sessioni diverse</p>
                  </div>
                  <Switch
                    checked={formData.semantic_memory_enabled}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, semantic_memory_enabled: checked }))}
                  />
                </div>
              </div>

              <PreCallHooksSection
                hooks={formData.pre_call_hooks || { inject_datetime: true, auto_crm_lookup: true }}
                onChange={(hooks) => setFormData(prev => ({ ...prev, pre_call_hooks: hooks }))}
                agentType="assistant"
              />
          </div>
        )}

        {activeTab === 'knowledge' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg border border-border">
              <div>
                <Label>Knowledge Base RAG</Label>
                <p className="text-xs text-muted-foreground">Risposte basate sui tuoi documenti</p>
              </div>
              <Switch
                checked={formData.rag_enabled}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, rag_enabled: checked }))}
              />
            </div>

            {formData.rag_enabled && (
              <div className="flex items-center justify-between p-3 rounded-lg border border-border ml-4 border-l-2 border-blue-300">
                <div className="flex items-center gap-1.5">
                  <Label>Sintesi Nativa</Label>
                  <div className="relative group">
                    <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-64 p-2 bg-gray-900 text-white text-[10px] rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 pointer-events-none">
                      <p className="font-medium mb-1">ON: L'agente ragiona direttamente sui dati recuperati dalla Knowledge Base. Più veloce, più naturale, costo inferiore.</p>
                      <p>OFF: I dati vengono prima sintetizzati da un modello ausiliario (GPT-4o-mini) e poi passati all'agente. Più controllata, utile con modelli costosi.</p>
                    </div>
                  </div>
                </div>
                <Switch
                  checked={formData.rag_synthesis_mode === 'native'}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, rag_synthesis_mode: checked ? 'native' : 'pre_synthesized', rag_inferential_enabled: !checked }))}
                />
              </div>
            )}

            {formData.rag_enabled && (
              isEdit && assistant?.id ? (
                <KnowledgeBaseManager agentId={assistant.id} />
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Salva prima l'assistente per gestire la Knowledge Base</p>
                </div>
              )
            )}
          </div>
        )}

        {activeTab === 'context' && (
          <div className="space-y-4">
              {!isEdit ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Globe className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Salva prima l'assistente per gestire il contesto</p>
                </div>
              ) : (
                <>
                  <Card className="border-border">
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Globe className="h-4 w-4" />
                        Sincronizzazione Contesto Tenant
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Sincronizza il contesto del tenant per fornire all'assistente informazioni aggiornate sulla configurazione del sistema.
                      </p>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-muted rounded-lg border border-border p-3">
                          <div className="text-xs text-muted-foreground mb-1">Ultima sincronizzazione</div>
                          <div className="text-sm font-medium">
                            {formData.tenant_context_synced_at
                              ? new Date(formData.tenant_context_synced_at).toLocaleString('it-IT')
                              : 'Mai sincronizzato'}
                          </div>
                        </div>
                        <div className="bg-muted rounded-lg border border-border p-3">
                          <div className="text-xs text-muted-foreground mb-1">Chunks contesto</div>
                          <div className="text-sm font-medium">{formData.chunk_count || 0}</div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Categorie disponibili:</Label>
                        <div className="grid grid-cols-2 gap-2">
                          {contextItems.map((item) => {
                            const syncCat = syncResult?.categories?.find((c) => c.label === item);
                            return (
                              <div key={item} className="flex items-center justify-between p-2 bg-muted rounded border border-border">
                                <div className="flex items-center gap-2">
                                  <CheckCircle2 className={`h-4 w-4 ${syncCat ? 'text-green-500' : 'text-muted-foreground'}`} />
                                  <span className="text-sm text-foreground">{item}</span>
                                </div>
                                {syncCat && (
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">{syncCat.records} rec</span>
                                    <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">{syncCat.chunks} ch</span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {syncResult && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-2">
                          <div className="flex items-center gap-2 text-green-700 font-medium text-sm">
                            <CheckCircle2 className="h-4 w-4" />
                            Sincronizzazione completata
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-xs">
                            <div className="bg-card rounded p-2 text-center border border-green-100">
                              <div className="font-bold text-green-800 text-lg">{syncResult.total_records}</div>
                              <div className="text-green-600">Record totali</div>
                            </div>
                            <div className="bg-card rounded p-2 text-center border border-green-100">
                              <div className="font-bold text-purple-800 text-lg">{syncResult.chunks_created}</div>
                              <div className="text-purple-600">Chunks creati</div>
                            </div>
                            <div className="bg-card rounded p-2 text-center border border-green-100">
                              <div className="font-bold text-blue-800 text-lg">{syncResult.embedding_dimensions || 0}</div>
                              <div className="text-blue-600">Dim. embeddings</div>
                            </div>
                          </div>
                          <div className="text-xs text-green-600">
                            {syncResult.total_characters?.toLocaleString()} caratteri elaborati da {syncResult.categories?.length} categorie
                          </div>
                        </div>
                      )}

                      <Button
                        onClick={handleSyncContext}
                        disabled={isSyncingContext}
                        className="w-full"
                      >
                        {isSyncingContext ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <RefreshCw className="h-4 w-4 mr-2" />
                        )}
                        {isSyncingContext ? 'Sincronizzazione in corso...' : 'Sincronizza Contesto'}
                      </Button>
                    </CardContent>
                  </Card>
                </>
              )}
          </div>
        )}
        {activeTab === 'delegation' && (
          <div className="space-y-4">
            {!isEdit || !assistant?.id ? (
              <div className="text-center py-8 text-muted-foreground">
                <ShieldCheck className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Salva prima l'assistente per configurare la delega dei poteri</p>
              </div>
            ) : (
              <PaDelegationPanel assistantId={assistant.id} configurationDirty={authorityDirty || configuration.loading || !!configuration.error} disabled={isSaving || !configuration.catalog?.can_manage} />
            )}
          </div>
        )}
      </fieldset>
      <KbModelChangeGuardDialog state={kbGuard.state} />
    </AgentConfigShell>
  );
}
